import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Signal, signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FileSystemFileEntry, NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';

import { Utils } from '../../services/utils.service';

import { SHARED_MODULES } from '../../../shared.';
import { SanitizerPipe } from '../../pipes/sanitizer.pipe';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { ProductPhotoPickModalComponent } from './product-photo-pick-modal/product-photo-pick-modal.component';
import {
	deriveImageFileNameFromSrc,
	formatProductBarcodeList,
	resolveImageByteSize,
	revokeBlobObjectUrl
} from './product-photo.utils';
import { FileSizePipe } from '../../pipes/file-size.pipe';

export interface ProductPhotoSelect {
	id: number;
	name: string;
	barcode: string;
	article: string;
	isMasterPosition?: boolean;
	width: number;
	height: number;
}

export interface UploadPhotoDialogData {
	product: ProductPhotoSelect;
	photo: unknown;
	title?: string;
	allowInternet?: boolean;
}

const PHOTO_PICK_MODAL_CONFIG = {
	width: '930px',
	height: 'auto',
	maxWidth: '96vw',
	autoFocus: false,
	backdropClass: 'back-drop'
} as const;

const PHOTO_PICK_MODAL_HANDOFF_CONFIG = {
	...PHOTO_PICK_MODAL_CONFIG,
	hasBackdrop: true
} as const;

type PhotoSourceType = 'internet' | 'pc' | undefined;

@Component({
	selector: 'app-upload-photo',
	templateUrl: './upload-photo.component.html',
	styleUrls: ['./upload-photo.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		SHARED_MODULES,
		NgxFileDropModule,
		SanitizerPipe,
		PhButtonComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		FileSizePipe
	],
	standalone: true
})
export class UploadPhotoComponent {
	private readonly matData: UploadPhotoDialogData = inject<UploadPhotoDialogData>(MAT_DIALOG_DATA);
	private readonly dialogRef: MatDialogRef<UploadPhotoComponent, string | false> = inject(MatDialogRef<UploadPhotoComponent, string | false>);
	private readonly dialog: MatDialog = inject(MatDialog);
	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	readonly product: WritableSignal<ProductPhotoSelect> = signal<ProductPhotoSelect>(this.matData.product);

	readonly selectedSource: WritableSignal<PhotoSourceType> = signal<PhotoSourceType>(
		this.hasInitialPcPhoto() ? 'pc' : undefined
	);

	readonly uploadedImage: WritableSignal<File | undefined> = signal<File | undefined>(undefined);
	readonly uploadedImageUrl: WritableSignal<string> = signal<string>('');
	readonly existingImageSizeBytes: WritableSignal<number | null> = signal<number | null>(null);
	readonly photoRemoved: WritableSignal<boolean> = signal<boolean>(false);

	readonly productBarcodeText: Signal<string> = computed((): string => formatProductBarcodeList(this.product()?.barcode));
	readonly productArticleText: Signal<string> = computed((): string => this.product()?.article);
	readonly showProductBarcodes: Signal<boolean> = computed((): boolean => Boolean(this.productBarcodeText()) && !this.product()?.isMasterPosition);
	readonly showProductArticle: Signal<boolean> = computed((): boolean => Boolean(this.productArticleText()) && !this.product()?.isMasterPosition);
	readonly canUseInternet: Signal<boolean> = computed((): boolean => Boolean(this.product()) && this.matData.allowInternet !== false);

	readonly pcTileDragDepth: WritableSignal<number> = signal<number>(0);
	readonly pcTileDropActive: Signal<boolean> = computed((): boolean => this.pcTileDragDepth() > 0);
	readonly pcDropWarning: WritableSignal<boolean> = signal<boolean>(false);
	readonly currentFileError: WritableSignal<boolean> = signal<boolean>(false);

	readonly currentFileImage: Signal<string> = computed((): string => {
		const uploadedUrl: string = this.uploadedImageUrl();
		if (uploadedUrl) {
			return uploadedUrl;
		}
		if (this.photoRemoved()) {
			return '';
		}
		const photo: unknown = this.matData.photo;
		return typeof photo === 'string' ? photo : '';
	});

	readonly tileIconColor: string = '#FF5A00';
	private openFileSelectorFn: (() => void) | null = null;

	readonly displayedFileName: Signal<string> = computed((): string => {
		const uploaded: File | undefined = this.uploadedImage();
		if (uploaded?.name) return uploaded.name;
		return deriveImageFileNameFromSrc(this.currentFileImage());
	});

	readonly displayedFileSizeBytes: Signal<number> = computed((): number =>
		this.uploadedImage()?.size ?? this.existingImageSizeBytes() ?? 0
	);

	readonly canContinue: Signal<boolean> = computed((): boolean => {
		const source: PhotoSourceType = this.selectedSource();
		if (source === 'internet') return this.canUseInternet();
		if (source === 'pc') return Boolean(this.currentFileImage()) && !this.currentFileError();
		return false;
	});

	constructor() {
		this.destroyRef.onDestroy((): void => revokeBlobObjectUrl(this.uploadedImageUrl()));
		void this.resolveExistingImageSize();
	}

	private hasInitialPcPhoto(): boolean {
		const photo: unknown = this.matData.photo;
		return typeof photo === 'string' && photo.trim().length > 0;
	}

	onNoClick(): void {
		this.dialogRef.close(false);
	}

	cancel(): void {
		this.dialogRef.close(false);
	}

	selectInternet(): void {
		if (!this.canUseInternet()) {
			return;
		}
		this.selectedSource.set('internet');
	}

	chooseFile(files: NgxFileDropEntry[]): void {
		this.pcTileDragDepth.set(0);
		this.selectedSource.set('pc');

		this.currentFileError.set(false);
		this.pcDropWarning.set(false);
		const droppedFileName: string = files[0]?.fileEntry?.name;

		if (!files.length || !droppedFileName) {
			this.pcDropWarning.set(true);
			return;
		}

		if (!Utils.bruteCheckFileExtension(droppedFileName)) {
			// Keep the current photo/selection; just show a warning for the dropped file.
			/*revokeBlobObjectUrl(this.uploadedImageUrl());
			this.uploadedImage.set(undefined);
			this.uploadedImageUrl.set('');
			this.photoRemoved.set(true);
			this.existingImageSizeBytes.set(null);*/
			this.pcDropWarning.set(true);
			return;
		}

		(files[0].fileEntry as FileSystemFileEntry).file((file: File): void => {
			revokeBlobObjectUrl(this.uploadedImageUrl());

			this.photoRemoved.set(false);
			this.uploadedImage.set(file);
			this.uploadedImageUrl.set(URL.createObjectURL(file));

			this.currentFileError.set(false);
		});
	}

	google(): void {
		const p: ProductPhotoSelect = this.product();
		if (!p || !this.canUseInternet()) {
			return;
		}
		const photoPickRef: MatDialogRef<ProductPhotoPickModalComponent> = this.dialog.open(
			ProductPhotoPickModalComponent,
			{
				...PHOTO_PICK_MODAL_HANDOFF_CONFIG,
				data: {
					product: p
				}
			}
		);
		this.handoffToDialog(photoPickRef);
	}

	onFileColumnClick(openFileSelector: () => void): void {
		this.openFileSelectorFn = openFileSelector;
		this.selectedSource.set('pc');
		this.pcDropWarning.set(false);
		if (this.currentFileImage() && !this.currentFileError()) {
			return;
		}
		openFileSelector();
	}

	onImageError(): void {
		this.currentFileError.set(true);
		revokeBlobObjectUrl(this.uploadedImageUrl());
		this.uploadedImageUrl.set('');
	}

	onContinueClick(): void {
		const source: PhotoSourceType = this.selectedSource();

		if (source === 'internet') {
			void this.google();
			return;
		}

		if (source === 'pc') {
			if (this.currentFileError()) {
				this.openFileSelectorFn?.();
				return;
			}

			const imageSrc: string = this.currentFileImage();

			if (!imageSrc) {
				this.openFileSelectorFn?.();
				return;
			}

			this.openPcPhotoEditor(imageSrc);
		}
	}

	onPcTileDragEnter(event: DragEvent): void {
		event.preventDefault();
		this.selectedSource.set('pc');
		this.pcTileDragDepth.update((d: number): number => d + 1);
	}

	onPcTileDragOver(event: DragEvent): void {
		event.preventDefault();
	}

	onPcTileDragLeave(): void {
		this.pcTileDragDepth.update((d: number): number => Math.max(0, d - 1));
	}

	removePcPhoto(event: MouseEvent): void {
		event.stopPropagation();

		revokeBlobObjectUrl(this.uploadedImageUrl());

		this.selectedSource.set(undefined);
		this.uploadedImage.set(undefined);
		this.uploadedImageUrl.set('');
		this.photoRemoved.set(true);
		this.existingImageSizeBytes.set(null);
		this.pcDropWarning.set(false);
		this.currentFileError.set(false);
	}

	private async resolveExistingImageSize(): Promise<void> {
		const src: string = this.currentFileImage();

		if (!src || this.uploadedImage()) {
			this.existingImageSizeBytes.set(null);
			return;
		}

		try {
			this.existingImageSizeBytes.set(await resolveImageByteSize(src));
		}
		catch {
			this.existingImageSizeBytes.set(null);
			this.currentFileError.set(true);
		}
	}

	private handoffToDialog(childDialogRef: MatDialogRef<any, string | false>): void {
		this.dialogRef.addPanelClass('upload-photo-dialog--handoff');
		childDialogRef.beforeClosed().subscribe((result: string | false): void => {
			this.dialogRef.close(result || false);
		});
	}

	private openPcPhotoEditor(imageSrc: string): void {
		const p: ProductPhotoSelect = this.product();
		const editorRef: MatDialogRef<ProductPhotoPickModalComponent> = this.dialog.open(
			ProductPhotoPickModalComponent,
			{
				...PHOTO_PICK_MODAL_HANDOFF_CONFIG,
				data: {
					product: p,
					initialImageSrc: imageSrc
				}
			}
		);
		this.handoffToDialog(editorRef);
	}
}